import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { bookingFormQuery, useCreateBooking } from '@/entities/booking'
import { newIdempotencyKey, type BookingDetail, type Messenger } from '@/shared/api'
import { nightsBetween, parseRubles, toKopecks } from './money'

// Предел доменный (`MaxNotesRunes`) и объявлен в контракте, но здесь он копией числа: сгенерированные
// типы ограничений не несут — `maxLength` живёт в схеме, а в TypeScript приезжает просто `string`.
// То же число и по той же причине стоит в окне правки брони.
const NOTES_LIMIT = 5000

/**
 * Поля формы — все строками, как их держит `<input>`. Числа появляются только на выходе
 * (`buildBody`): `guests: 2` в состоянии значит, что поле нельзя очистить — человек стирает «2»,
 * получает `NaN`, и в поле выезжает `NaN` или пустота, смотря как повезёт.
 */
export type BookingForm = {
  objectId: string
  checkIn: string
  checkOut: string
  /**
   * КОД площадки; `null` — не выбрана (в контракте поле необязательно).
   *
   * ⚠️ Тип СТРОКА, а не `BookingSource`: тот — запись справочника (код, имя, цвет), а на запись
   * уходит только код; имя и цвет подставляет сервер — они и так его.
   */
  platformCode: string | null
  guestName: string
  guestPhone: string
  guestChannel: Messenger | null
  guests: string
  total: string
  /**
   * Деньги, принятые менеджером ПРЯМО при заведении. ⚠️ Это не «предоплата площадки» — та живёт в
   * её данных и описывает ЕЁ слова. Здесь касса компании, и уезжает она в `initialPayments`.
   */
  prepay: string
  deposit: string
  internalNotes: string
}

/**
 * ⚠️ ЧЕГО ЗДЕСЬ НАМЕРЕННО НЕТ. Макет открывает модалку с семью заполненными полями
 * (`prototype/Prototype.dc.html:3902`); взято одно.
 *
 * ✔ Гостей `2` — видимое редактируемое число, и у брони гостей всё равно не ноль.
 *
 * ✘ Залог `5000` — поле есть, дефолта нет: залог уезжает настоящим платежом в кассу компании, и
 *   предзаполненная сумма означала бы «менеджер принял пять тысяч», которых он не принимал. Пока
 *   поле было мёртвым, дефолт не врал никому; теперь врал бы деньгами.
 *
 * ✘ Даты — в макете это литеральные константы, демо-данные, а не правило: формулы там нет вовсе.
 *   Придумать её самим значит УГАДАТЬ, когда приезжает гость, — единственное поле, где неверный
 *   дефолт молча создаёт бронь не на те сутки.
 *
 * ✘ Площадка и канал — оба необязательны в контракте, то есть «не указано» предусмотрено. Выбранная
 *   за менеджера площадка — ложная атрибуция источника, которая уедет в аналитику и в карточку; а
 *   выбранный канал хуже: приложение по нему ПИШЕТ гостю, и оставивший Telegram не получит первое
 *   сообщение вовсе. Оба чипа при этом нарисованы как в макете и выбранными, и невыбранными.
 *
 * `objectId` пуст здесь, но не на экране: он выводится из справочника, а состоянием становится,
 * только когда его трогает человек.
 */
const EMPTY: BookingForm = {
  objectId: '',
  checkIn: '',
  checkOut: '',
  platformCode: null,
  guestName: '',
  guestPhone: '',
  guestChannel: null,
  guests: '2',
  total: '',
  prepay: '',
  deposit: '',
  internalNotes: '',
}

/**
 * Тело `POST /bookings` из полей формы. Рубли превращаются в копейки ровно здесь и больше нигде.
 *
 * Необязательные поля не отправляются пустыми: `guestPhone: ''` — это не «телефона нет», а «телефон
 * это пустая строка», и сервер записал бы её гостю в контакт. Нулевые суммы тоже: бронь без
 * введённой суммы — это бронь, сумма которой ещё не назначена, а не бронь за 0 ₽.
 */
export function buildBody(form: BookingForm) {
  const totalRubles = parseRubles(form.total)
  const guests = Number.parseInt(form.guests, 10)
  const initialPayments = buildInitialPayments(form)

  return {
    objectId: form.objectId,
    checkIn: form.checkIn,
    checkOut: form.checkOut,
    guestName: form.guestName.trim(),
    /*
     * ⚠️ ПЛОЩАДКА НЕ ВЫБРАНА — ЗНАЧИТ БРОНЬ ЗАВЕЛИ РУКАМИ, И ЭТО ФАКТ, А НЕ ДОГАДКА: её только что
     * напечатали в нашей же форме. `manual` — настоящая запись справочника домена, а не выдуманный
     * код. Без неё домен оставил бы происхождение пустым (`if s.OriginPlatformCode != ""` в
     * конверторе), и список показал бы бронь БЕЗ источника — ту самую, что сам создал минуту назад.
     */
    originPlatformCode: form.platformCode ?? 'manual',
    ...(form.guestPhone.trim() ? { guestPhone: form.guestPhone.trim() } : {}),
    ...(form.guestChannel ? { guestChannel: form.guestChannel } : {}),
    ...(Number.isFinite(guests) && guests > 0 ? { guestsCount: guests } : {}),
    ...(totalRubles > 0 ? { total: toKopecks(totalRubles) } : {}),
    ...(form.internalNotes.trim() ? { internalNotes: form.internalNotes.trim() } : {}),
    ...(initialPayments.length > 0 ? { initialPayments } : {}),
  }
}

/**
 * Деньги, принятые менеджером при заведении.
 *
 * ⚠️ ОДНИМ ВЫЗОВОМ, А НЕ ДВУМЯ: домен пишет их в ОДНОЙ транзакции с бронью, поэтому исход «бронь
 * создалась, залог потерялся» невозможен. Вторым запросом после создания мы вернули бы ровно тот
 * исход, из-за которого эти поля с формы и снимали, только теперь с сетевой причиной.
 *
 * Ноль не отправляем — это «не принимал», а не «принял ноль». `direction` у обоих `income`: при
 * заведении менеджер деньги ПРИНИМАЕТ, а возвраты и штрафы записываются позже, на живой броне.
 */
function buildInitialPayments(form: BookingForm) {
  const prepayRubles = parseRubles(form.prepay)
  const depositRubles = parseRubles(form.deposit)

  return [
    ...(prepayRubles > 0
      ? [{ amount: toKopecks(prepayRubles), kind: 'payment' as const, direction: 'income' as const }]
      : []),
    ...(depositRubles > 0
      ? [{ amount: toKopecks(depositRubles), kind: 'deposit' as const, direction: 'income' as const }]
      : []),
  ]
}

export type BookingCreate = ReturnType<typeof useBookingCreate>

/**
 * Форма «Новая бронь». Главное свойство фичи не про форму, а про КЛЮЧ ИДЕМПОТЕНТНОСТИ.
 *
 * Он рождается ОДИН раз — на первое нажатие «Создать бронь» — и приходит с каждым следующим, пока
 * бронь не создана. Клиент не отличает «сервер не получил» от «получил, создал, а ответ потерялся»:
 * на экране в обоих случаях один тост. Единственное, что спасает живого гостя от ВТОРОЙ брони, —
 * тот же ключ в повторе (разбор — `shared/api/idempotency.ts`).
 *
 * ⚠️ ЗАЩИТ ДВЕ, И ОДНОЙ ЛЮБОЙ ИЗ НИХ МАЛО. `inFlight` — РЕФ, а не `isPending`: состояние React
 * приезжает следующим рендером, и два клика в одном тике оба увидели бы `false`. Он гасит двойной
 * КЛИК, но не повтор ПОСЛЕ сбоя — от него страхует стабильный ключ; а один ключ оставил бы два
 * лишних запроса и гонку двух ответов.
 *
 * ⚠️ ЗЕРКАЛО ПРАВИЛА: ПОСЛЕ УСПЕХА КЛЮЧ ГАСНЕТ. Переиспользованный старый вернул бы первую бронь и
 * МОЛЧА не создал вторую, отрапортовав об успехе. Полагаться на размонтирование модалки как на
 * сброс состояния в этом проекте уже пробовали, и это стоило [Critical] — гасим явно.
 */
export function useBookingCreate(onCreated: (booking: BookingDetail) => void) {
  const [form, setForm] = useState<BookingForm>(EMPTY)

  /** Ключ ТЕКУЩЕГО действия. `null` — ещё ни разу не нажимали (или уже создали). */
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)

  /** Летит ли запрос ПРЯМО СЕЙЧАС. Реф, а не состояние: см. разбор выше. */
  const inFlight = useRef(false)

  const reference = useQuery(bookingFormQuery())
  const create = useCreateBooking()

  const set = <K extends keyof BookingForm>(field: K, value: BookingForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }))

  const objects = reference.data?.objects ?? []

  /*
   * Объект ВЫВОДИТСЯ, а не синхронизируется эффектом: пока человек не трогал `<select>`, показываем
   * первый объект компании — ровно то, что показал бы браузер сам. Эффект на приход справочника дал
   * бы то же самое, но с лишним рендером и с классом багов «состояние разъехалось с источником».
   */
  const objectId = form.objectId || (objects[0]?.id ?? '')

  const nights = nightsBetween(form.checkIn, form.checkOut)

  /*
   * Правило макета дословно: ночей > 0 И имя гостя непусто (`nbCanCreate`, `prototype/Prototype.dc.html:7180`). Площадка,
   * канал и суммы не блокируют — они необязательны и в контракте.
   *
   * `objectId` добавлен к ним, и это не отступление: он ОБЯЗАТЕЛЕН в `CreateBooking`, а пустым бывает
   * ровно тогда, когда справочник летит, упал или у компании нет ни одной квартиры. Во всех трёх
   * случаях бронь создать не на что, и живая кнопка отправила бы `objectId: ''`.
   */
  const datesAndName = nights > 0 && form.guestName.trim().length > 0
  /*
   * ⚠️ ПРЕДЕЛ СЧИТАЕТ СИМВОЛЫ, А НЕ БАЙТЫ: `[...text]`, а не `text.length` — суррогатная пара это
   * один символ, а `.length` даст два. Обрезаем по краям, как нормализация домена.
   *
   * ⚠️ НЕ `maxLength` НА ПОЛЕ (то же решение, что в окне правки): атрибут молча отрезал бы хвост
   * вставленного текста, и автор узнал бы о потере, только перечитав. Гасим кнопку и называем
   * причину — отказ надо ПОКАЗАТЬ.
   */
  const notesOver = Math.max(0, [...form.internalNotes.trim()].length - NOTES_LIMIT)
  const canCreate = datesAndName && objectId !== '' && notesOver === 0

  /*
   * ⚠️ ПРИЧИНА ВЫВОДИТСЯ ЗДЕСЬ ЖЕ, ИЗ ТЕХ ЖЕ УСЛОВИЙ, И ЭТО ПОЧИНКА, А НЕ СТИЛЬ. Правило получило
   * третье условие (`objectId`), а подпись кнопки осталась зашитой в `NewBookingForm.tsx` — при
   * упавшем справочнике человек видел заполненные даты, заполненное имя и мёртвую кнопку, которая
   * требовала то, что он уже сделал. Разъехались они потому, что ЛЕЖАЛИ В РАЗНЫХ ФАЙЛАХ. Теперь
   * подпись не может соврать, не соврав сначала самой валидации; тест держит их вместе.
   *
   * Порядок веток не случаен: сперва то, что человек может сделать САМ, и лишь потом настоящая
   * причина — её иначе не угадать, она не про его действия вовсе. «Объекта нет» покрывает все три
   * случая пустого `objectId` и ни в одном не врёт: подробности человек читает в секции «Объект».
   *
   * Первая фраза — цитата макета (`prototype/Prototype.dc.html:2313`, `title` у погашенной кнопки);
   * остальные наши, потому что и условий у макета было одно.
   */
  const blockReason = canCreate
    ? null
    : !datesAndName
      ? 'Укажите даты и имя гостя'
      : notesOver > 0
        ? `Примечание длиннее предела в ${NOTES_LIMIT} знаков на ${notesOver} — уберите лишнее`
        : 'Объекта нет — бронь создать не на что'

  const submit = () => {
    if (!canCreate || inFlight.current) return

    // Ключ ЭТОГО действия: берём уже выданный, если нажимают повторно после сбоя.
    const key = idempotencyKey ?? newIdempotencyKey()
    if (idempotencyKey === null) setIdempotencyKey(key)

    inFlight.current = true

    void (async () => {
      try {
        const booking = await create.mutateAsync({
          body: { ...buildBody(form), objectId },
          idempotencyKey: key,
        })
        // Действие завершено — ключ отслужил. Следующая бронь получит свой.
        setIdempotencyKey(null)
        onCreated(booking)
      } catch {
        /*
         * ⚠️ ЛОВИМ ОБЯЗАТЕЛЬНО: `mutateAsync` реджектится, а unhandled rejection в этом приложении
         * уводит на экран смерти (`RootErrorBoundary` слушает `unhandledrejection`).
         *
         * Показывать нечего: тост с `X-Request-Id` печатает `MutationCache`. Форма остаётся со всем
         * набранным, КЛЮЧ ОСТАЁТСЯ ЖИВЫМ, и повтор — это просто второе нажатие той же кнопки;
         * отдельной кнопки «Повторить» в макете нет и заводить её не надо.
         */
      }
      // Замок снимаем после try/catch, не в `finally` (`tools/compiler-bailouts.mjs`): выхода нет.
      inFlight.current = false
    })()
  }

  return {
    form,
    set,
    /** Объект: либо выбранный человеком, либо первый из справочника. Пустой — справочника нет. */
    objectId,
    reference,
    nights,
    canCreate,
    /** Почему «Создать бронь» мертва — или `null`, если жива. Выводится из тех же условий. */
    blockReason,
    /** Летит ли создание. Для кнопки — она гаснет; для двойного клика есть `inFlight`. */
    creating: create.isPending,
    submit,
  }
}
